import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
    CreateExportLcRequest,
    CreateLcAmendmentRequest,
    CreateLcChecklistItemRequest,
    ExportLc,
    ExportLcListItem,
    LcAmendment,
    LcChecklistItem,
    LcStatus,
    PageResponse,
    UpdateExportLcRequest,
    UpdateLcChecklistItemRequest,
    UpdateLcStatusRequest,
} from '../interfaces/commercial.interface';

export interface ExportLcListParams {
    page?: number;
    size?: number;
    status?: LcStatus;
    bank?: string;
    buyer?: string;
    dueSoon?: boolean;
}

/** Returns data only — components own triggerDownload/showApiError per house rules. */
export const ExportLcService = {
    list: async (institutionId: string, params: ExportLcListParams = {}): Promise<PageResponse<ExportLcListItem>> => {
        const { page = 0, size = 20, status, bank, buyer, dueSoon } = params;
        const query = new URLSearchParams({ page: String(page), size: String(size) });
        if (status) query.set('status', status);
        if (bank) query.set('bank', bank);
        if (buyer) query.set('buyer', buyer);
        if (dueSoon !== undefined) query.set('dueSoon', String(dueSoon));
        return (await axios.get(`${ENDPOINTS.EXPORT_LC.LIST(institutionId)}?${query.toString()}`)).data;
    },
    getById: async (institutionId: string, lcId: string): Promise<ExportLc> =>
        (await axios.get(ENDPOINTS.EXPORT_LC.GET_BY_ID(institutionId, lcId))).data,
    create: async (institutionId: string, dto: CreateExportLcRequest): Promise<ExportLc> =>
        (await axios.post(ENDPOINTS.EXPORT_LC.CREATE(institutionId), dto)).data,
    update: async (institutionId: string, lcId: string, dto: UpdateExportLcRequest): Promise<ExportLc> =>
        (await axios.put(ENDPOINTS.EXPORT_LC.UPDATE(institutionId, lcId), dto)).data,
    updateStatus: async (institutionId: string, lcId: string, dto: UpdateLcStatusRequest): Promise<ExportLc> =>
        (await axios.put(ENDPOINTS.EXPORT_LC.UPDATE_STATUS(institutionId, lcId), dto)).data,
    remove: async (institutionId: string, lcId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.EXPORT_LC.DELETE(institutionId, lcId));
    },

    // ---- Amendments (R-6: append-only, GET/POST only, no update/delete method exists) ----------
    listAmendments: async (institutionId: string, lcId: string, page = 0, size = 20): Promise<PageResponse<LcAmendment>> =>
        (await axios.get(`${ENDPOINTS.EXPORT_LC.AMENDMENTS(institutionId, lcId)}?page=${page}&size=${size}`)).data,
    createAmendment: async (institutionId: string, lcId: string, dto: CreateLcAmendmentRequest): Promise<LcAmendment> =>
        (await axios.post(ENDPOINTS.EXPORT_LC.AMENDMENTS(institutionId, lcId), dto)).data,

    // ---- Document checklist -----------------------------------------------------------------
    listChecklist: async (institutionId: string, lcId: string, page = 0, size = 50): Promise<PageResponse<LcChecklistItem>> =>
        (await axios.get(`${ENDPOINTS.EXPORT_LC.CHECKLIST(institutionId, lcId)}?page=${page}&size=${size}`)).data,
    addChecklistItem: async (institutionId: string, lcId: string, dto: CreateLcChecklistItemRequest): Promise<LcChecklistItem> =>
        (await axios.post(ENDPOINTS.EXPORT_LC.CHECKLIST(institutionId, lcId), dto)).data,
    updateChecklistItem: async (
        institutionId: string, lcId: string, itemId: string, dto: UpdateLcChecklistItemRequest,
    ): Promise<LcChecklistItem> =>
        (await axios.put(ENDPOINTS.EXPORT_LC.CHECKLIST_ITEM(institutionId, lcId, itemId), dto)).data,
    removeChecklistItem: async (institutionId: string, lcId: string, itemId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.EXPORT_LC.CHECKLIST_ITEM(institutionId, lcId, itemId));
    },
};
